import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector, useGetActiveProfileName, useGetRubustechIoUrl } from '@/utils/hooks';
import {
    DateTimeDisplay,
    ExternalLink,
    MarkdownDisplay,
    Select,
    useAsyncState,
} from '@ygtechnologies/bygr-frontend-component-library';
import theme from './UserSettings.module.scss';
import { AcceptedTermsDto, getAcceptedTerms } from '@/services/terms';
import { Trans } from '@lingui/react/macro';

interface LegalProps {}
const LegalSummary: React.FC<LegalProps> = (props) => {
    const termsLink = useGetRubustechIoUrl('terms-of-use');
    const privacyLink = useGetRubustechIoUrl('privacy-policy');
    const userProfiles = useAppSelector((state) => state.global.profiles);
    const profileName = useGetActiveProfileName();

    const dto = useMemo(() => {
        return userProfiles?.map((profile) => ({
            profileId: profile.profileId,
            projectId: profile.projectId,
            residentialUnitId: profile.residentialUnitId,
        }));
    }, [userProfiles]);

    const user = useAppSelector((state) => state.global.user);

    const [acceptedTerms] = useAsyncState(() => getAcceptedTerms(dto ?? []), [dto], []);

    const [selectedTerm, setSelectedTerm] = useState<AcceptedTermsDto | null>(null);

    useEffect(() => {
        if (acceptedTerms.length > 0 && !selectedTerm) setSelectedTerm(acceptedTerms[0]);
    }, [acceptedTerms, selectedTerm]);

    return (
        <>
            <h5>
                <Trans>Legal Summary</Trans>
            </h5>

            <p className={theme.selectTitle}>
                <Trans>Unit and project</Trans>
            </p>

            <Select
                className={theme.select}
                options={acceptedTerms.map((term) => ({
                    label: term.residentialUnitName + ' - ' + term.projectName,
                    value: term.id,
                }))}
                value={
                    selectedTerm
                        ? {
                              label: selectedTerm.residentialUnitName + ' - ' + selectedTerm.projectName,
                              value: selectedTerm.id,
                          }
                        : null
                }
                onChange={(value: any) => setSelectedTerm(acceptedTerms.find((term) => term.id === value?.value) ?? null)}
            />

            {selectedTerm && (
                <div className={theme.legalNotice}>
                    <span className={theme.legalNoticeTitle}>
                        <Trans>Legal notice</Trans>
                    </span>
                    <div className={theme.legalNoticeDetails}>
                        <Trans>Signed:</Trans>
                        <DateTimeDisplay value={selectedTerm.acceptDate} />
                    </div>
                    <div className={theme.legalNoticeDetails}>
                        <Trans>Buyer:</Trans>
                        <span>{user?.displayName}</span>
                    </div>

                    {selectedTerm && <MarkdownDisplay className={theme.termsText} markdown={selectedTerm.termsText} />}
                </div>
            )}

            <div className={theme.legalLinks}>
                <ExternalLink href={termsLink} target="_blank">
                    <Trans>View terms and conditions</Trans>
                </ExternalLink>
                <ExternalLink href={privacyLink} target="_blank">
                    <Trans>View Rubus privacy policy</Trans>
                </ExternalLink>
                {selectedTerm && selectedTerm.privacyPolicyUrl && (
                    <ExternalLink href={selectedTerm.privacyPolicyUrl} target="_blank">
                        <Trans>View {profileName} Privacy policy</Trans>
                    </ExternalLink>
                )}
            </div>
        </>
    );
};

export default LegalSummary;
